import { useEffect, useMemo, useRef, useState } from "react";
import { Panel } from "@akaike/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { runCoverageV1RunsRunIdCoverageGet } from "../api/generated/qcsControlPlane";
import type { RunCoverage } from "../api/generated/model";
import { fmtInt } from "../api/labels";

// Two merchant-sweep coverage maps side by side with synchronized pan/zoom: the
// day-vs-night comparison view. Each panel renders one run's server-binned coverage
// (same grid logic as RunCoverageMap), but both share a single viewport: dragging or
// zooming either map mirrors the other, and a single bbox+bin drives both queries so
// the two grids stay cell-aligned and directly comparable.

const INDIA: L.LatLngTuple = [22.6, 80.0];
const MIN_BIN = 0.002; // mirrors the server floor (just under the 0.005° dense step)
const CELL_PX = 10; // target on-screen cell size: drives the zoom->bin mapping

function binForZoom(zoom: number): number {
  const degPerPx = 360 / (256 * 2 ** zoom);
  return Math.max(MIN_BIN, CELL_PX * degPerPx);
}

function round(n: number, dp = 4): number {
  const f = 10 ** dp;
  return Math.round(n * f) / f;
}

// Serviceable fraction -> hue from red (none) through amber to green (all serviceable).
function binColor(frac: number): string {
  const f = Math.max(0, Math.min(1, frac));
  return `hsl(${(8 + 132 * f).toFixed(0)}, 68%, 46%)`;
}

type View = { minLat: number; minLon: number; maxLat: number; maxLon: number; bin: number };

// One Leaflet map + its bin/store layers. The map instance and a "fit me to this
// extent" callback are handed back up so the parent can wire cross-map sync.
function useCoverageMap(runId: string) {
  const elRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const binsRef = useRef<L.LayerGroup | null>(null);
  const storesRef = useRef<L.LayerGroup | null>(null);
  return { elRef, mapRef, binsRef, storesRef, runId };
}

type MapHandle = ReturnType<typeof useCoverageMap>;

function draw(h: MapHandle, data: RunCoverage | undefined) {
  const map = h.mapRef.current;
  const bins = h.binsRef.current;
  const stores = h.storesRef.current;
  if (!map || !bins || !stores || !data) return;

  const half = (data.bin_size ?? MIN_BIN) / 2;
  bins.clearLayers();
  for (const [lat, lon, count, serv] of data.bins) {
    L.rectangle(
      [
        [lat - half, lon - half],
        [lat + half, lon + half],
      ],
      {
        stroke: false,
        fill: true,
        fillColor: binColor(count > 0 ? serv / count : 0),
        fillOpacity: 0.55,
        interactive: false,
      },
    ).addTo(bins);
  }

  stores.clearLayers();
  for (const st of data.stores) {
    const eta = st.eta_minutes != null ? ` · ${st.eta_minutes}m` : "";
    const name = st.name ? `<br/>${st.name}` : "";
    L.circleMarker([st.latitude, st.longitude], {
      radius: 5,
      color: "#1f6feb",
      weight: 1.5,
      fillColor: "#1f6feb",
      fillOpacity: 0.9,
    })
      .bindPopup(`<b>${st.store_id}</b>${eta}${name}`)
      .addTo(stores);
  }
}

export function SyncedCoverageMaps({
  runIdA,
  runIdB,
  labelA,
  labelB,
}: {
  runIdA: string;
  runIdB: string;
  labelA: string;
  labelB: string;
}) {
  const a = useCoverageMap(runIdA);
  const b = useCoverageMap(runIdB);
  const syncingRef = useRef(false); // recursion guard for mirrored move/zoom
  const fittedRef = useRef(false);
  const [view, setView] = useState<View | null>(null);

  // Shared viewport -> query string. Both runs query the SAME bbox+bin so their grids
  // line up cell-for-cell. Omitted on first paint so the server returns a coarse grid
  // over the data extent (which we fit-to-bounds and refine).
  const qs = useMemo(() => {
    if (!view) return "";
    return `?${new URLSearchParams({
      min_lat: String(view.minLat),
      min_lon: String(view.minLon),
      max_lat: String(view.maxLat),
      max_lon: String(view.maxLon),
      bin: String(view.bin),
    })}`;
  }, [view]);

  const params = view
    ? {
        min_lat: view.minLat,
        min_lon: view.minLon,
        max_lat: view.maxLat,
        max_lon: view.maxLon,
        bin: view.bin,
      }
    : undefined;
  const covA = useQuery({
    queryKey: ["compare-coverage", runIdA, qs],
    queryFn: () => runCoverageV1RunsRunIdCoverageGet(runIdA, params),
    placeholderData: keepPreviousData,
  });
  const covB = useQuery({
    queryKey: ["compare-coverage", runIdB, qs],
    queryFn: () => runCoverageV1RunsRunIdCoverageGet(runIdB, params),
    placeholderData: keepPreviousData,
  });

  // -- create both maps once + wire bidirectional sync --------------------------
  useEffect(() => {
    if (!a.elRef.current || !b.elRef.current || a.mapRef.current) return;
    const mk = (h: MapHandle) => {
      const map = L.map(h.elRef.current as HTMLElement, {
        attributionControl: false,
        preferCanvas: true,
        worldCopyJump: true,
      }).setView(INDIA, 5);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map);
      h.mapRef.current = map;
      h.binsRef.current = L.layerGroup().addTo(map);
      h.storesRef.current = L.layerGroup().addTo(map);
      return map;
    };
    const mapA = mk(a);
    const mapB = mk(b);

    // Mirror src->dst on every move/zoom frame; the guard stops the echo (dst's own
    // move event, fired synchronously by setView, is ignored).
    const mirror = (src: L.Map, dst: L.Map) =>
      src.on("move zoom", () => {
        if (syncingRef.current) return;
        syncingRef.current = true;
        dst.setView(src.getCenter(), src.getZoom(), { animate: false });
        syncingRef.current = false;
      });
    mirror(mapA, mapB);
    mirror(mapB, mapA);

    // Map A is the canonical viewport source for the data queries (B tracks it).
    const sync = () => {
      const bnds = mapA.getBounds().pad(0.25);
      setView({
        minLat: round(bnds.getSouth()),
        minLon: round(bnds.getWest()),
        maxLat: round(bnds.getNorth()),
        maxLon: round(bnds.getEast()),
        bin: round(binForZoom(mapA.getZoom()), 6),
      });
    };
    mapA.on("moveend", sync);

    setTimeout(() => {
      mapA.invalidateSize();
      mapB.invalidateSize();
    }, 0);
    return () => {
      mapA.remove();
      mapB.remove();
      a.mapRef.current = null;
      b.mapRef.current = null;
      fittedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -- fit once to the union of both runs' extents ------------------------------
  useEffect(() => {
    const mapA = a.mapRef.current;
    if (!mapA || fittedRef.current) return;
    const exts = [covA.data?.extent, covB.data?.extent].filter(Boolean) as [
      number,
      number,
      number,
      number,
    ][];
    if (exts.length === 0) return;
    fittedRef.current = true;
    const s = Math.min(...exts.map((e) => e[0]));
    const w = Math.min(...exts.map((e) => e[1]));
    const n = Math.max(...exts.map((e) => e[2]));
    const e = Math.max(...exts.map((ex) => ex[3]));
    if (n > s && e > w) mapA.fitBounds([[s, w], [n, e]], { padding: [24, 24], maxZoom: 13 });
    else mapA.setView([s, w], 12);
  }, [covA.data, covB.data, a.mapRef]);

  // -- redraw each panel when its data changes ----------------------------------
  useEffect(() => draw(a, covA.data), [covA.data, a]);
  useEffect(() => draw(b, covB.data), [covB.data, b]);

  return (
    <Panel style={{ gridColumn: "span 12" }}>
      <Panel.Head title="Coverage maps · synced pan &amp; zoom">
        <div className="flex-1" />
        <Legend />
      </Panel.Head>
      <Panel.Body tight>
        <div className="row" style={{ gap: 10, alignItems: "stretch", flexWrap: "wrap" }}>
          <MapPanel handle={a} label={labelA} cov={covA.data} error={covA.isError} />
          <MapPanel handle={b} label={labelB} cov={covB.data} error={covB.isError} />
        </div>
      </Panel.Body>
    </Panel>
  );
}

function MapPanel({
  handle,
  label,
  cov,
  error,
}: {
  handle: MapHandle;
  label: string;
  cov: RunCoverage | undefined;
  error: boolean;
}) {
  const c = cov?.counts;
  const probed = c?.probed ?? 0;
  return (
    <div className="col" style={{ flex: "1 1 360px", minWidth: 300, gap: 6 }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
        <span className="mono" style={{ fontSize: 12, fontWeight: 600 }}>
          {label}
        </span>
        {c && (
          <span className="num faint" style={{ fontSize: 10.5 }}>
            {fmtInt(c.probed)} probed · {fmtInt(c.serviceable)} serv · {fmtInt(c.stores)} stores
          </span>
        )}
      </div>
      <div style={{ position: "relative" }}>
        <div
          ref={handle.elRef}
          style={{ width: "100%", height: 480, borderRadius: 6, overflow: "hidden", zIndex: 0 }}
        />
        {error ? (
          <Overlay>Could not load coverage (is ClickHouse configured?).</Overlay>
        ) : probed === 0 ? (
          <Overlay>No coverage recorded for this run.</Overlay>
        ) : null}
      </div>
    </div>
  );
}

function Legend() {
  const swatch = (color: string, label: string) => (
    <span className="row" style={{ gap: 5, alignItems: "center" }}>
      <span
        style={{ width: 11, height: 11, borderRadius: 2, background: color, display: "inline-block" }}
      />
      <span className="faint" style={{ fontSize: 10 }}>
        {label}
      </span>
    </span>
  );
  return (
    <div className="row" style={{ gap: 12, flexWrap: "wrap" }}>
      {swatch(binColor(1), "serviceable")}
      {swatch(binColor(0.5), "mixed")}
      {swatch(binColor(0), "not serviceable")}
      {swatch("#1f6feb", "store")}
    </div>
  );
}

function Overlay({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="muted"
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12.5,
        pointerEvents: "none",
        background: "rgba(0,0,0,0.04)",
        zIndex: 400,
      }}
    >
      {children}
    </div>
  );
}
